import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import MovieCard from '@/components/feature/MovieCard';
import Reveal from '@/components/feature/Reveal';
import { specials } from '@/mocks/specials';
import { reviews } from '@/mocks/reviews';
import { getMovieById } from '@/mocks/movies';
import useSeo from '@/hooks/useSeo';

export default function Special() {
  const [activeId, setActiveId] = useState(specials[0]?.id ?? '');

  useSeo({
    title: '影评专题 - 美国伦理片深度影评与策划片单',
    description:
      '汇集美国伦理题材电影的深度影评与专题策划片单，从道德困境、家庭肖像等主题切入，提供有观点的解读与推荐，满足影迷的深度阅读需求。',
    keywords: '伦理片影评,美国伦理片专题,伦理电影解读,美国伦理电影推荐',
  });

  const active = useMemo(() => specials.find((s) => s.id === activeId), [activeId]);

  const essays = useMemo(
    () => (active ? active.essayIds.map((eid) => reviews.find((r) => r.id === eid)).filter(Boolean) : []),
    [active],
  );

  const picks = useMemo(
    () =>
      active
        ? active.picks
            .map((p) => ({ movie: getMovieById(p.movieId), reason: p.reason }))
            .filter((p) => p.movie)
        : [],
    [active],
  );

  const related = useMemo(
    () => (active ? active.relatedIds.map((rid) => specials.find((s) => s.id === rid)).filter(Boolean) : []),
    [active],
  );

  return (
    <Layout>
      {/* 头部 */}
      <section className="px-4 md:px-8 pt-8 pb-8 border-b border-background-200">
        <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-4">影评专题</span>
        <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">深度影评与专题策划</h1>
        <p className="text-sm md:text-base leading-8 text-foreground-600 max-w-3xl">
          我们相信好的伦理电影值得被认真讨论。这里集合了编辑部撰写的深度影评与主题片单，希望带你越过情节本身，看见每一部作品真正想追问的东西。
        </p>
      </section>

      {specials.length === 0 ? (
        <div className="px-4 md:px-8 py-24 text-center text-foreground-500">暂无专题内容，敬请期待</div>
      ) : (
        <>
          {/* 专题切换 */}
          <div className="px-4 md:px-8 pt-6 flex items-center gap-2 flex-wrap">
            {specials.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setActiveId(s.id)}
                className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${activeId === s.id ? 'bg-primary-500 text-background-50' : 'bg-background-50 border border-background-200 text-foreground-700 hover:border-primary-300'}`}
              >
                {s.title.split('：')[0]}
              </button>
            ))}
          </div>

          {active && (
            <>
              {/* 专题主视觉 */}
              <section className="px-4 md:px-8 py-8">
                <div className="relative w-full h-[240px] md:h-[320px] rounded-lg overflow-hidden bg-foreground-950">
                  <img src={active.cover} alt={active.title} title={`${active.title} - 美国伦理片专题`} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/50 to-foreground-950/20" />
                  <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8">
                    <h2 className="text-xl md:text-3xl font-bold text-background-50 mb-3 max-w-3xl">{active.title}</h2>
                    <p className="text-sm md:text-base leading-7 text-background-100/85 max-w-3xl line-clamp-3">{active.intro}</p>
                  </div>
                </div>
              </section>

              {/* 影评列表 */}
              <section className="px-4 md:px-8 py-6">
                <h3 className="text-xl font-bold text-foreground-950 mb-5 flex items-center gap-2">
                  <i className="ri-article-line text-primary-500" /> 专题影评
                </h3>
                {essays.length === 0 ? (
                  <p className="text-sm text-foreground-500">暂无影评内容</p>
                ) : (
                  <div className="space-y-5">
                    {essays.map((r, i) => (
                      <Reveal key={r!.id} delay={i * 60}>
                        <Link
                          to={`/detail/${r!.movieId}`}
                          className="group flex flex-col sm:flex-row gap-5 rounded-lg bg-background-50 border border-background-200 overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
                        >
                          <div className="w-full sm:w-56 h-40 sm:h-auto shrink-0 overflow-hidden bg-background-200">
                            <img src={r!.cover} alt={r!.title} title={`${r!.title} - ${r!.movieTitle}`} className="w-full h-full object-top group-hover:scale-105 transition-transform duration-500" />
                          </div>
                          <div className="flex-1 p-5 sm:py-5 sm:pr-6 min-w-0">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-900 text-xs">关联影片 · {r!.movieTitle}</span>
                              <span className="text-xs text-foreground-400">{r!.date}</span>
                            </div>
                            <h4 className="text-base md:text-lg font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors">{r!.title}</h4>
                            <p className="text-sm leading-7 text-foreground-500 line-clamp-2">{r!.excerpt}</p>
                            <div className="mt-3 flex items-center gap-3 text-xs text-foreground-400">
                              <span className="flex items-center gap-1"><i className="ri-user-line" /> {r!.author}</span>
                              <span className="flex items-center gap-1"><i className="ri-time-line" /> {r!.readingTime} 分钟</span>
                            </div>
                          </div>
                        </Link>
                      </Reveal>
                    ))}
                  </div>
                )}
              </section>

              {/* 策划片单 */}
              <section className="px-4 md:px-8 py-8 bg-background-50 border-y border-background-200">
                <h3 className="text-xl font-bold text-foreground-950 mb-5 flex items-center gap-2">
                  <i className="ri-play-list-2-line text-primary-500" /> 策划片单
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {picks.map((p) => (
                    <div key={p.movie!.id}>
                      <MovieCard movie={p.movie!} />
                      <p className="mt-2.5 text-xs leading-6 text-foreground-500 px-0.5">{p.reason}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* 相关专题 */}
              {related.length > 0 && (
                <section className="px-4 md:px-8 py-8">
                  <h3 className="text-sm font-semibold text-foreground-700 mb-3">相关专题</h3>
                  <div className="flex items-center gap-3 flex-wrap">
                    {related.map((s) => (
                      <button
                        key={s!.id}
                        type="button"
                        onClick={() => setActiveId(s!.id)}
                        className="px-4 py-2 rounded-md bg-secondary-50 border border-secondary-200 text-sm text-secondary-800 hover:bg-secondary-100 transition-colors cursor-pointer"
                      >
                        {s!.title}
                      </button>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </>
      )}
    </Layout>
  );
}